import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';
import { sessionQueryOptions } from '@/shared/session/currentUser';

import { authApi, sessionQueryKey } from './api';
import type { LoginPayload, PasswordChange, User } from './types';

/**
 * Current session, with the options every reader of it shares (`sessionQueryOptions`:
 * the request, no retry on a 401, a minute fresh).
 */
export function useSession() {
  return useQuery(sessionQueryOptions);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, LoginPayload>({
    mutationFn: authApi.login,
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: authApi.logout,
    onSettled: () => {
      // Whatever the server answered, this browser no longer holds a session.
      queryClient.clear();
    },
  });
}

/** Change one's own password. The session object is patched by the caller. */
export function useChangePassword() {
  return useMutation<User, ApiError, PasswordChange>({ mutationFn: authApi.changePassword });
}
